'use client';

import { useMemo, useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { Mail01Icon } from '@hugeicons/core-free-icons';
import { api } from '@loamify/shared-utils';
import { isValidTwoFactorBackupCodeInput } from '@loamify/core-domain';
import {
  Alert,
  AlertDescription,
  AuthFullscreenLayout,
  Button,
  FormField,
  IconInput,
  Input,
  PasswordInput,
} from '@loamify/ui';
import { useAuth, canAccessAdminTools } from '@/lib/auth-store';

type AdminUser = { id: string; email: string; capabilities?: string[] };
type LoginStep = 'credentials' | 'email-verify' | 'two-factor' | 'mfa-enrollment';

function isAdminUser(user: unknown): user is AdminUser {
  return (
    typeof user === 'object' &&
    user !== null &&
    'id' in user &&
    'email' in user &&
    typeof (user as AdminUser).id === 'string' &&
    typeof (user as AdminUser).email === 'string'
  );
}

function canAccessAdmin(user: AdminUser | null | undefined) {
  return canAccessAdminTools(user);
}

export default function AdminLoginPage() {
  const router = useRouter();
  const user = useAuth((s) => s.user);
  const hydrated = useAuth((s) => s.hydrated);
  const [step, setStep] = useState<LoginStep>('credentials');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [pendingToken, setPendingToken] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [busyMessage, setBusyMessage] = useState('Signing you in…');
  const [mfaSetupSecret, setMfaSetupSecret] = useState('');
  const [mfaSetupOtpauthUrl, setMfaSetupOtpauthUrl] = useState('');
  const [mfaEnrollmentCode, setMfaEnrollmentCode] = useState('');
  const [mfaBackupCodes, setMfaBackupCodes] = useState<string[]>([]);

  useEffect(() => {
    if (mfaBackupCodes.length > 0) return;
    if (hydrated && user && canAccessAdmin(user)) {
      router.replace('/dashboard');
    }
  }, [hydrated, user, router, mfaBackupCodes.length]);

  useEffect(() => {
    if (step !== 'mfa-enrollment' || !pendingToken || mfaSetupSecret) return;
    let cancelled = false;
    setError('');
    setBusyMessage('Preparing authenticator setup…');
    setSubmitting(true);
    void api
      .adminMfaEnrollmentSetup(pendingToken)
      .then((res) => {
        if (cancelled) return;
        setMfaSetupSecret(res.secret);
        setMfaSetupOtpauthUrl(res.otpauthUrl);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Could not start 2FA setup');
      })
      .finally(() => {
        if (!cancelled) setSubmitting(false);
      });
    return () => {
      cancelled = true;
    };
  }, [step, pendingToken, mfaSetupSecret]);

  const { title, description, heroTitle, onBack } = useMemo(() => {
    switch (step) {
      case 'email-verify':
        return {
          title: 'Verify your email',
          description:
            'Enter the 6-digit code sent to your email. In local dev, check the API terminal log.',
          heroTitle: 'Secure admin access',
          onBack: () => {
            setStep('credentials');
            setPendingToken('');
            setVerificationCode('');
            setError('');
          },
        };
      case 'two-factor':
        return {
          title: 'Two-factor authentication',
          description: 'Enter the code from your authenticator app.',
          heroTitle: 'Secure admin access',
          onBack: () => {
            setStep('credentials');
            setPendingToken('');
            setTwoFactorCode('');
            setError('');
          },
        };
      case 'mfa-enrollment':
        return {
          title: 'Enable two-factor authentication',
          description:
            'Admin sessions require an authenticator app. Complete enrollment here before entering the admin console.',
          heroTitle: 'Secure admin access',
          onBack: () => {
            setStep('credentials');
            setPendingToken('');
            setMfaSetupSecret('');
            setMfaSetupOtpauthUrl('');
            setMfaEnrollmentCode('');
            setMfaBackupCodes([]);
            setError('');
          },
        };
      default:
        return {
          title: 'Admin portal',
          description: 'Platform operations are available only to users with admin capabilities.',
          heroTitle: 'LOAMIFY platform operations',
          onBack: undefined,
        };
    }
  }, [step]);

  function finishLogin(user: unknown): Promise<boolean> {
    if (!isAdminUser(user)) {
      return Promise.reject(new Error('Login failed'));
    }
    if (!canAccessAdmin(user)) {
      setError('Admin access required.');
      return Promise.resolve(false);
    }
    return useAuth
      .getState()
      .confirmSession(user)
      .then(() => {
        router.replace('/dashboard');
        return true;
      })
      .catch(() => {
        throw new Error('Your admin session could not be verified. Please try signing in again.');
      });
  }

  async function onSubmitCredentials(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setBusyMessage('Signing you in…');
    setSubmitting(true);
    let navigated = false;
    try {
      const res = await api.adminLogin(email.trim(), password);
      if (res.requiresEmailVerification && res.pendingToken) {
        setPendingToken(res.pendingToken);
        setVerificationCode('');
        setStep('email-verify');
        return;
      }
      if (res.requiresTwoFactor && res.pendingToken) {
        setPendingToken(res.pendingToken);
        setTwoFactorCode('');
        setStep('two-factor');
        return;
      }
      if (res.requiresTwoFactorEnrollment && res.pendingToken) {
        setPendingToken(res.pendingToken);
        setMfaSetupSecret('');
        setMfaSetupOtpauthUrl('');
        setMfaEnrollmentCode('');
        setMfaBackupCodes([]);
        setStep('mfa-enrollment');
        return;
      }
      if (!res.user) throw new Error('Login failed');
      navigated = await finishLogin(res.user);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      if (!navigated) setSubmitting(false);
    }
  }

  async function onSubmitEmailVerification(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setBusyMessage('Verifying…');
    setSubmitting(true);
    let navigated = false;
    try {
      const res = await api.adminLoginVerifyEmail(pendingToken, verificationCode.trim());
      if (res.requiresTwoFactor && res.pendingToken) {
        setPendingToken(res.pendingToken);
        setTwoFactorCode('');
        setStep('two-factor');
        return;
      }
      if (res.requiresTwoFactorEnrollment && res.pendingToken) {
        setPendingToken(res.pendingToken);
        setMfaSetupSecret('');
        setMfaSetupOtpauthUrl('');
        setMfaEnrollmentCode('');
        setMfaBackupCodes([]);
        setStep('mfa-enrollment');
        return;
      }
      if (!res.user) throw new Error('Verification failed');
      navigated = await finishLogin(res.user);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed');
    } finally {
      if (!navigated) setSubmitting(false);
    }
  }

  async function onSubmitTwoFactor(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setBusyMessage('Verifying…');
    setSubmitting(true);
    let navigated = false;
    try {
      const res = await api.adminLoginTwoFactor(pendingToken, twoFactorCode.trim());
      if (!res.user) throw new Error('Verification failed');
      navigated = await finishLogin(res.user);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed');
    } finally {
      if (!navigated) setSubmitting(false);
    }
  }

  async function onResendVerificationCode() {
    setError('');
    setBusyMessage('Sending code…');
    setSubmitting(true);
    try {
      await api.resendLoginEmailVerification(pendingToken);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not resend code');
    } finally {
      setSubmitting(false);
    }
  }

  async function onSubmitMfaEnrollment(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setBusyMessage('Enabling two-factor authentication…');
    setSubmitting(true);
    let navigated = false;
    try {
      const res = await api.adminMfaEnrollmentConfirm(pendingToken, mfaEnrollmentCode.trim());
      if (!res.user) throw new Error('Enrollment failed');
      if (res.backupCodes?.length) {
        setMfaBackupCodes(res.backupCodes);
        await useAuth.getState().confirmSession(res.user as AdminUser);
        return;
      }
      navigated = await finishLogin(res.user);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Enrollment failed');
    } finally {
      if (!navigated) setSubmitting(false);
    }
  }

  let body: React.ReactNode = null;

  if (step === 'mfa-enrollment') {
    body = (
      <div className="space-y-5">
        {mfaBackupCodes.length > 0 ? (
          <div className="space-y-4">
            <Alert>
              <AlertDescription className="space-y-2">
                <p>Save these backup codes in a secure place. Each code works once if you lose your authenticator.</p>
                <p className="font-mono text-xs">{mfaBackupCodes.join(' · ')}</p>
              </AlertDescription>
            </Alert>
            {error ? (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            ) : null}
            <Button
              type="button"
              className="h-10 w-full font-semibold"
              disabled={submitting}
              onClick={() => {
                setError('');
                const auth = useAuth.getState();
                if (
                  auth.sessionStatus === 'authenticated' &&
                  auth.user &&
                  canAccessAdmin(auth.user)
                ) {
                  router.replace('/dashboard');
                  return;
                }
                setBusyMessage('Opening admin console…');
                setSubmitting(true);
                void finishLogin(auth.user)
                  .then((ok) => {
                    if (!ok && !useAuth.getState().user) {
                      setError('Admin access required.');
                    }
                  })
                  .catch((err) => {
                    setError(err instanceof Error ? err.message : 'Could not finish sign in');
                  })
                  .finally(() => setSubmitting(false));
              }}
            >
              Continue to dashboard
            </Button>
          </div>
        ) : (
          <>
            <Alert>
              <AlertDescription>
                Scan the setup key in your authenticator app, then enter the 6-digit code to finish admin enrollment.
              </AlertDescription>
            </Alert>
            {mfaSetupSecret ? (
              <div className="rounded-lg border px-4 py-3">
                <p className="text-xs font-medium uppercase tracking-wide text-neutral-500">Setup key</p>
                <p className="mt-1 break-all font-mono text-sm">{mfaSetupSecret}</p>
                {mfaSetupOtpauthUrl ? (
                  <p className="mt-2 text-xs text-neutral-500">
                    Or open this URL in your authenticator app: {mfaSetupOtpauthUrl}
                  </p>
                ) : null}
              </div>
            ) : null}
            <form onSubmit={onSubmitMfaEnrollment} className="space-y-4">
              <FormField label="Authentication code" htmlFor="admin-mfa-enrollment-code" required>
                <Input
                  id="admin-mfa-enrollment-code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="6-digit code"
                  maxLength={6}
                  value={mfaEnrollmentCode}
                  onChange={(e) => setMfaEnrollmentCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  required
                  disabled={submitting || !mfaSetupSecret}
                />
              </FormField>
              {error ? (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              ) : null}
              <Button
                className="h-10 w-full font-semibold"
                type="submit"
                disabled={submitting || !mfaSetupSecret || mfaEnrollmentCode.length !== 6}
              >
                {submitting ? 'Enabling…' : 'Enable 2FA and continue'}
              </Button>
            </form>
          </>
        )}
        <Button type="button" variant="outline" className="h-10 w-full" onClick={onBack}>
          Return to admin sign in
        </Button>
      </div>
    );
  } else if (step === 'email-verify') {
    body = (
      <form onSubmit={onSubmitEmailVerification} className="space-y-5">
        <FormField label="Verification code" htmlFor="verification-code" required>
          <Input
            id="verification-code"
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="6-digit code"
            maxLength={6}
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            required
            disabled={submitting}
          />
        </FormField>
        {error ? (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}
        <Button
          className="h-10 w-full font-semibold"
          type="submit"
          disabled={submitting || verificationCode.length !== 6}
        >
          {submitting ? 'Verifying…' : 'Verify and continue'}
        </Button>
        <Button
          type="button"
          variant="outline"
          className="h-10 w-full"
          disabled={submitting}
          onClick={() => void onResendVerificationCode()}
        >
          Resend code
        </Button>
      </form>
    );
  } else if (step === 'two-factor') {
    const canSubmitTwoFactor = useBackupCode
      ? isValidTwoFactorBackupCodeInput(twoFactorCode)
      : twoFactorCode.replace(/\D/g, '').length === 6;

    body = (
      <form onSubmit={onSubmitTwoFactor} className="space-y-5">
        {useBackupCode ? (
          <FormField label="Backup code" htmlFor="two-factor-backup-code" required>
            <Input
              id="two-factor-backup-code"
              autoComplete="off"
              placeholder="XXXX-XXXX"
              value={twoFactorCode}
              onChange={(e) => setTwoFactorCode(e.target.value.toUpperCase())}
              disabled={submitting}
            />
          </FormField>
        ) : (
          <FormField label="Authentication code" htmlFor="two-factor-code" required>
            <Input
              id="two-factor-code"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="6-digit code"
              maxLength={6}
              value={twoFactorCode}
              onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              required
              disabled={submitting}
            />
          </FormField>
        )}
        <div className="flex justify-center">
          <button
            type="button"
            className="text-body-sm font-medium text-foreground hover:underline"
            onClick={() => {
              setUseBackupCode((current) => !current);
              setTwoFactorCode('');
            }}
            disabled={submitting}
          >
            {useBackupCode ? 'Use authenticator code instead' : 'Use a backup code instead'}
          </button>
        </div>
        {error ? (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}
        <Button
          className="h-10 w-full font-semibold"
          type="submit"
          disabled={submitting || !canSubmitTwoFactor}
        >
          {submitting ? 'Verifying…' : 'Verify and continue'}
        </Button>
      </form>
    );
  } else {
    body = (
      <form onSubmit={onSubmitCredentials} className="space-y-5">
        <FormField label="Email" htmlFor="admin-login-email" required>
          <IconInput
            id="admin-login-email"
            type="email"
            icon={Mail01Icon}
            autoComplete="email"
            placeholder="admin@loamify.io"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={submitting}
          />
        </FormField>
        <FormField label="Password" htmlFor="admin-login-password" required>
          <PasswordInput
            id="admin-login-password"
            autoComplete="current-password"
            placeholder="Enter your password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            disabled={submitting}
          />
        </FormField>
        {error ? (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}
        <Button type="submit" className="h-10 w-full font-semibold" disabled={submitting}>
          {submitting ? 'Signing in…' : 'Sign in'}
        </Button>
      </form>
    );
  }

  const footer = undefined;

  return (
    <AuthFullscreenLayout
      title={title}
      description={description}
      heroTitle={heroTitle}
      onBack={onBack}
      busy={submitting}
      busyMessage={busyMessage}
      stepKey={`admin-login-${step}`}
      footer={footer}
    >
      {body}
    </AuthFullscreenLayout>
  );
}
